import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../lib/utils';
import Button from './Button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  const navigation = [
    { name: 'Home', href: '/' },
    {
      name: 'Leistungen',
      href: '/leistungen',
      hasDropdown: true,
      dropdown: [
        { name: 'Alle Leistungen', href: '/leistungen' },
        { name: 'Gebäudemanagement', href: '/leistungen/gebaeudemanagement' },
        { name: 'Personalüberlassung', href: '/leistungen/arbeitnehmerueberlassung' },
        { name: 'Event-Aufbau', href: '/leistungen/event-aufbau' },
        { name: 'Abbrucharbeiten', href: '/leistungen/abbrucharbeiten' },
        { name: 'Bauendreinigung', href: '/leistungen/bauendreinigung' },
        { name: 'Eventpersonal', href: '/leistungen/eventpersonal' },
      ]
    },
    { name: 'Über uns', href: '/ueber-uns' },
    { name: 'Kontakt', href: '/kontakt' },
  ];

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm py-3'
          : 'bg-white py-4'
      )}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="text-2xl font-bold text-primary-text-dark">
              SENKAY
            </div>
            <div className="hidden sm:block text-sm text-primary-text-muted">
              Personal & Logistik
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navigation.map((item) => (
              <div key={item.name} className="relative group">
                {item.hasDropdown ? (
                  <>
                    <Link
                      to={item.href}
                      className="nav-link px-4 py-2 rounded-lg flex items-center"
                    >
                      {item.name}
                      <ChevronDown className="w-4 h-4 ml-1" />
                    </Link>
                    {/* Dropdown Menu */}
                    <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-soft border border-primary-gray-border opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                      <div className="py-2">
                        {item.dropdown.map((dropItem) => (
                          <Link
                            key={dropItem.name}
                            to={dropItem.href}
                            className="block px-4 py-2 text-sm text-primary-text-dark hover:bg-primary-gray-light transition-colors"
                          >
                            {dropItem.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={cn(
                      'nav-link px-4 py-2 rounded-lg',
                      location.pathname === item.href && 'bg-primary-gray-light'
                    )}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            <Link to="/kontakt">
              <Button variant="primary" size="sm">
                Angebot anfordern
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-primary-gray-light transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-primary-gray-border pt-4">
            <div className="flex flex-col space-y-2">
              {navigation.map((item) => (
                <div key={item.name}>
                  {item.hasDropdown ? (
                    <>
                      <button
                        onClick={() => setIsServicesOpen(!isServicesOpen)}
                        className="w-full flex items-center justify-between px-4 py-2 text-left nav-link rounded-lg"
                      >
                        {item.name}
                        <ChevronDown
                          className={cn(
                            'w-4 h-4 transition-transform',
                            isServicesOpen && 'rotate-180'
                          )}
                        />
                      </button>
                      {isServicesOpen && (
                        <div className="ml-4 mt-2 space-y-2">
                          {item.dropdown.map((dropItem) => (
                            <Link
                              key={dropItem.name}
                              to={dropItem.href}
                              className="block px-4 py-2 text-sm text-primary-text-dark hover:bg-primary-gray-light rounded-lg transition-colors"
                            >
                              {dropItem.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href}
                      className={cn(
                        'block px-4 py-2 nav-link rounded-lg',
                        location.pathname === item.href && 'bg-primary-gray-light'
                      )}
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}
              <div className="pt-4">
                <Link to="/kontakt" className="block">
                  <Button variant="primary" className="w-full">
                    Angebot anfordern
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
